import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Checkbox,
    Confirm,
    DataTable,
    Modal,
    MultiSelect,
    Searchbox,
    Spinner,
} from "@tag/tag-components-react-v4";
import SurveyJSTemplateService from "../services/SurveyJSTemplateService";
import TenantProvisioningHistoryModal, { HistoryTarget } from "./TenantProvisioningHistoryModal";
import {
    BulkProvisioningJobStatusDto,
    SurveyJSSyncMetadataDto,
    SurveyJSTemplateDto,
    SurveyJSTemplateTypeDto,
    TemplateProvisioningResult,
    TenantProvisioningRowDto,
} from "../types/SurveyJSTemplate";
import { ProjectAccents } from "../../General/config/projectAccents";
import UploadTemplateDialog from "./UploadTemplateDialog";
import TemplateHistoryModal from "./TemplateHistoryModal";
import styles from "./SurveyJSTemplates.module.scss";

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatShortDate(value: unknown): string {
    if (typeof value !== "string" || value.length === 0) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${String(d.getFullYear()).slice(2)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function formatShortDateOnly(value: unknown): string {
    if (typeof value !== "string" || value.length === 0) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
}

type DisplayStatus = "OnCurrent" | "Outdated" | "Failed" | "NotProvisioned";

const DISPLAY_STATUS_OPTIONS: { name: string; value: DisplayStatus }[] = [
    { name: "On current version", value: "OnCurrent" },
    { name: "Outdated", value: "Outdated" },
    { name: "Failed", value: "Failed" },
    { name: "Not provisioned", value: "NotProvisioned" },
];

function displayStatusFromRecord(row: TenantProvisioningRowDto): DisplayStatus {
    if (row.syncedVersion != null) return row.isOnCurrentVersion ? "OnCurrent" : "Outdated";
    if (row.status === "Failed") return "Failed";
    return "NotProvisioned";
}

type SourceLabel = "HSC" | "External";

function sourceLabelFor(row: TenantProvisioningRowDto): SourceLabel | null {
    if (row.syncedVersion == null) return null;
    if (row.status === "Success" && row.provisionedVersion === row.syncedVersion) return "HSC";
    return "External";
}

function tenantKey(corpSiteName: string, clientGuidId: string): string {
    return `${corpSiteName}|${clientGuidId}`;
}

function triggerBrowserDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function labelForStatus(s: DisplayStatus): string {
    return s === "OnCurrent" ? "On current"
        : s === "Outdated" ? "Outdated"
            : s === "Failed" ? "Failed"
                : "Not provisioned";
}

function summarizeChildStatuses(statuses: DisplayStatus[]): string {
    if (statuses.length === 0) return "";
    const counts = new Map<DisplayStatus, number>();
    statuses.forEach(s => counts.set(s, (counts.get(s) ?? 0) + 1));
    const entries = Array.from(counts.entries());
    if (entries.length === 1) {
        const only = entries[0][0];
        return statuses.length === 1
            ? labelForStatus(only)
            : `${statuses.length} ${labelForStatus(only).toLowerCase()}`;
    }
    return entries
        .map(([s, n]) => `${n} ${labelForStatus(s).toLowerCase()}`)
        .join(" · ");
}

function rollupStatusOf(statuses: DisplayStatus[]): DisplayStatus | "Partial" {
    if (statuses.length === 0) return "NotProvisioned";
    const distinct = new Set(statuses);
    if (distinct.size === 1) return statuses[0];
    if (distinct.has("Failed")) return "Partial";
    return "Partial";
}

function dotClassForStatus(s: DisplayStatus | "Partial" | null): string {
    return s === "OnCurrent" ? styles.StatusDotSuccess
        : s === "Outdated" || s === "Partial" ? styles.StatusDotOutdated
            : s === "Failed" ? styles.StatusDotFailed
                : styles.StatusDotMissing;
}

interface TreeRow {
    kind: 'tenant' | 'template';
    rowKey: string;
    subRows?: TreeRow[];

    // Common
    nameCell: string;

    // Tenant fields
    corpSiteName?: string;
    databaseName?: string;
    childStatuses?: DisplayStatus[];

    // Template fields
    moduleKey?: string;
    moduleDisplayName?: string;
    typeDisplayName?: string;
    templateGuid?: string;
    templateStatus?: DisplayStatus;
    provisionedVersion?: string | null;
    syncedVersion?: string | null;
    provisionedDateUtc?: string | null;
    provisionedByName?: string | null;
    triggerKind?: string | null;
    sourceLabel?: SourceLabel | null;
    errorMessage?: string | null;
    isOnCurrentVersion?: boolean;
    tenantDisplayNameForAction?: string;
    tenantCorpSiteForAction?: string;
    tenantClientGuidIdForAction?: string;
    tenantClientIdForAction?: number;
}

interface ProvisionTarget {
    templateGuid: string;
    moduleKey: string;
    templateName: string;
    templateVersion: string;
    corpSiteName: string;
    clientGuidId: string;
    tenantDisplayName: string;
    isOnCurrentVersion: boolean;
}

function SurveyJSTemplates() {
    const [types, setTypes] = useState<SurveyJSTemplateTypeDto[]>([]);
    const [activeTemplates, setActiveTemplates] = useState<SurveyJSTemplateDto[]>([]);
    const [tenantsByTemplate, setTenantsByTemplate] = useState<Record<string, TenantProvisioningRowDto[]>>({});
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState<{ type: any; message: string } | undefined>();

    const [uploadVisible, setUploadVisible] = useState(false);
    const [historyType, setHistoryType] = useState<{ typeKey: string; typeDisplayName: string } | undefined>();

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState<DisplayStatus[]>([]);
    const [corpSiteFilter, setCorpSiteFilter] = useState<string[]>([]);

    const [rowsInFlight, setRowsInFlight] = useState<Set<string>>(new Set());
    const [templatesInFlight, setTemplatesInFlight] = useState<Set<string>>(new Set());
    const [confirmTarget, setConfirmTarget] = useState<ProvisionTarget | undefined>();
    const [confirmForce, setConfirmForce] = useState(false);
    const [deactivateTarget, setDeactivateTarget] = useState<SurveyJSTemplateDto | undefined>();
    const [historyTarget, setHistoryTarget] = useState<HistoryTarget | null>(null);

    const [bulkForce, setBulkForce] = useState(false);
    const [bulkLaunching, setBulkLaunching] = useState(false);
    const [bulkJobs, setBulkJobs] = useState<BulkProvisioningJobStatusDto[]>([]);

    const [expandedTenants, setExpandedTenants] = useState<Set<string>>(new Set());

    const [syncing, setSyncing] = useState(false);
    const [lastSync, setLastSync] = useState<SurveyJSSyncMetadataDto | null>(null);

    const toggleTenant = useCallback((rowKey: string) => {
        setExpandedTenants(prev => {
            const next = new Set(prev);
            if (next.has(rowKey)) next.delete(rowKey);
            else next.add(rowKey);
            return next;
        });
    }, []);

    const loadTypes = useCallback(async () => {
        try {
            const list = await SurveyJSTemplateService.listTypes(false);
            setTypes(list);
            return list;
        } catch (e: any) {
            setAlert({ type: "error", message: e?.message || "Failed to load template types." });
            return [] as SurveyJSTemplateTypeDto[];
        }
    }, []);

    const loadActiveTemplates = useCallback(async () => {
        try {
            const list = await SurveyJSTemplateService.listActiveTemplates();
            setActiveTemplates(list);
            return list;
        } catch (e: any) {
            setAlert({ type: "error", message: e?.message || "Failed to load active templates." });
            return [] as SurveyJSTemplateDto[];
        }
    }, []);

    const loadTenantsForActives = useCallback(async (actives: SurveyJSTemplateDto[]) => {
        const entries = await Promise.all(actives.map(async (t) => {
            try {
                const rows = await SurveyJSTemplateService.getTenantProvisioning(t.templateGuid);
                return [t.templateGuid, rows] as const;
            } catch {
                return [t.templateGuid, [] as TenantProvisioningRowDto[]] as const;
            }
        }));
        const next: Record<string, TenantProvisioningRowDto[]> = {};
        for (const [guid, rows] of entries) next[guid] = rows;
        setTenantsByTemplate(next);
    }, []);

    const loadSyncStatus = useCallback(async () => {
        try {
            const meta = await SurveyJSTemplateService.getSyncStatus();
            setLastSync(meta);
        } catch {
            // Non-fatal — the page is still usable without sync status.
        }
    }, []);

    const loadAll = useCallback(async () => {
        setLoading(true);
        setAlert(undefined);
        await Promise.all([loadTypes(), loadSyncStatus()]);
        const actives = await loadActiveTemplates();
        if (actives.length > 0) {
            await loadTenantsForActives(actives);
        } else {
            setTenantsByTemplate({});
        }
        setLoading(false);
    }, [loadTypes, loadActiveTemplates, loadTenantsForActives, loadSyncStatus]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const handleSync = useCallback(async () => {
        setSyncing(true);
        setAlert(undefined);
        try {
            const meta = await SurveyJSTemplateService.syncTenants();
            setLastSync(meta);
            const msg = meta.status === "Success"
                ? `Sync complete — ${meta.tenantsProcessed} tenants, ${meta.rowsUpdated} rows updated.`
                : `Sync completed with ${meta.tenantsFailed} tenant failures — ${meta.tenantsProcessed} ok, ${meta.rowsUpdated} rows updated.`;
            setAlert({ type: meta.status === "Success" ? "success" : "warning", message: msg });
            const actives = await loadActiveTemplates();
            if (actives.length > 0) await loadTenantsForActives(actives);
        } catch (e: any) {
            setAlert({ type: "error", message: e?.message || "Sync failed." });
        } finally {
            setSyncing(false);
        }
    }, [loadActiveTemplates, loadTenantsForActives]);

    // Best-effort full sync (every tenant × every active template). Used after
    // bulk provisioning, which already touches every tenant. Does not overwrite
    // a prior success alert unless the sync itself surfaces a problem.
    const syncTenantsAfterProvision = useCallback(async () => {
        setSyncing(true);
        try {
            const meta = await SurveyJSTemplateService.syncTenants();
            setLastSync(meta);
            if (meta.status !== "Success") {
                setAlert({
                    type: "warning",
                    message: `Provisioning done. Post-provision sync reported ${meta.tenantsFailed} tenant failure(s).`,
                });
            }
        } catch (e: any) {
            setAlert({
                type: "warning",
                message: `Provisioning done. Post-provision sync failed: ${e?.message || "unknown error"}.`,
            });
        } finally {
            setSyncing(false);
        }
    }, []);

    // Targeted sync for the single tenant we just provisioned to. Lighter than
    // a full sync (one tenant, no global metadata write) and runs synchronously
    // so the tenant row reflects the fresh install before the page refreshes.
    const syncTenantAfterProvision = useCallback(async (corpSiteName: string, clientGuidId: string) => {
        try {
            await SurveyJSTemplateService.syncTenant(corpSiteName, clientGuidId);
        } catch (e: any) {
            setAlert({
                type: "warning",
                message: `Provisioning done. Post-provision sync for this tenant failed: ${e?.message || "unknown error"}.`,
            });
        }
    }, []);

    // Build tree rows: one parent per distinct tenant across all active templates,
    // children = one row per (tenant, active template) pair.
    const treeRows = useMemo<TreeRow[]>(() => {
        if (activeTemplates.length === 0) return [];

        // Index all tenant rows by tenant key
        type Bag = { rep: TenantProvisioningRowDto; perTemplate: { template: SurveyJSTemplateDto; row: TenantProvisioningRowDto }[] };
        const byTenant = new Map<string, Bag>();

        for (const t of activeTemplates) {
            const rows = tenantsByTemplate[t.templateGuid] ?? [];
            for (const r of rows) {
                const key = tenantKey(r.corpSiteName, r.clientGuidId);
                if (!byTenant.has(key)) byTenant.set(key, { rep: r, perTemplate: [] });
                byTenant.get(key)!.perTemplate.push({ template: t, row: r });
            }
        }

        const out: TreeRow[] = [];
        const entries = Array.from(byTenant.entries());
        for (const [key, bag] of entries) {
            const childStatuses = bag.perTemplate.map((x: { template: SurveyJSTemplateDto; row: TenantProvisioningRowDto }) => displayStatusFromRecord(x.row));
            const subRows: TreeRow[] = bag.perTemplate.map(({ template, row }: { template: SurveyJSTemplateDto; row: TenantProvisioningRowDto }) => ({
                kind: 'template',
                rowKey: `${key}|${template.templateGuid}`,
                nameCell: `${template.moduleDisplayName || template.module} / ${template.typeDisplayName || template.templateType} — ${template.templateName} v${template.version}`,
                moduleKey: template.module,
                moduleDisplayName: template.moduleDisplayName,
                typeDisplayName: template.typeDisplayName,
                templateGuid: template.templateGuid,
                templateStatus: displayStatusFromRecord(row),
                provisionedVersion: row.provisionedVersion,
                syncedVersion: row.syncedVersion,
                provisionedDateUtc: row.provisionedDateUtc,
                provisionedByName: row.provisionedByName,
                triggerKind: row.triggerKind,
                sourceLabel: sourceLabelFor(row),
                errorMessage: row.errorMessage,
                isOnCurrentVersion: row.isOnCurrentVersion,
                tenantDisplayNameForAction: row.displayName,
                tenantCorpSiteForAction: row.corpSiteName,
                tenantClientGuidIdForAction: row.clientGuidId,
                tenantClientIdForAction: row.clientId,
            }));

            out.push({
                kind: 'tenant',
                rowKey: key,
                nameCell: bag.rep.displayName,
                corpSiteName: bag.rep.corpSiteName,
                databaseName: bag.rep.databaseName,
                childStatuses,
                subRows,
            });
        }
        return out.sort((a, b) =>
            (a.corpSiteName ?? "").localeCompare(b.corpSiteName ?? "") ||
            (a.nameCell ?? "").localeCompare(b.nameCell ?? "")
        );
    }, [activeTemplates, tenantsByTemplate]);

    const distinctCorpSites = useMemo(
        () => Array.from(new Set(treeRows.map(t => t.corpSiteName ?? "").filter(Boolean))).sort(),
        [treeRows]
    );

    const filteredTenantRows = useMemo(() => {
        const needle = searchTerm.trim().toLowerCase();
        return treeRows.filter(t => {
            if (corpSiteFilter.length > 0 && !corpSiteFilter.includes(t.corpSiteName ?? "")) return false;
            if (needle) {
                const hay = `${t.nameCell} ${t.corpSiteName ?? ""} ${t.databaseName ?? ""}`.toLowerCase();
                if (!hay.includes(needle)) return false;
            }
            if (statusFilter.length > 0) {
                const cs = t.childStatuses ?? [];
                if (!cs.some(s => statusFilter.includes(s))) return false;
            }
            return true;
        });
    }, [treeRows, searchTerm, statusFilter, corpSiteFilter]);

    const visibleRows = useMemo(() => {
        const out: TreeRow[] = [];
        for (const tenant of filteredTenantRows) {
            out.push(tenant);
            if (expandedTenants.has(tenant.rowKey)) {
                for (const child of tenant.subRows ?? []) out.push(child);
            }
        }
        return out;
    }, [filteredTenantRows, expandedTenants]);

    // ── Handlers ───────────────────────────────────────────────────────────

    const handleUploaded = async (template: SurveyJSTemplateDto) => {
        setUploadVisible(false);
        setAlert({ type: "success", message: `Uploaded ${template.templateName} v${template.version}.` });
        await loadAll();
    };

    const handleActivate = async (template: SurveyJSTemplateDto) => {
        setTemplatesInFlight(prev => new Set(prev).add(template.templateGuid));
        try {
            await SurveyJSTemplateService.activateTemplate(template.templateGuid);
            setAlert({ type: "success", message: `Activated ${template.templateName} v${template.version}.` });
            await loadAll();
        } catch (e: any) {
            setAlert({ type: "error", message: e?.message || "Activate failed." });
        } finally {
            setTemplatesInFlight(prev => {
                const next = new Set(prev);
                next.delete(template.templateGuid);
                return next;
            });
        }
    };

    const handleDeactivateConfirmed = async () => {
        if (!deactivateTarget) return;
        const target = deactivateTarget;
        setDeactivateTarget(undefined);
        setTemplatesInFlight(prev => new Set(prev).add(target.templateGuid));
        try {
            await SurveyJSTemplateService.deactivateTemplate(target.templateGuid);
            setAlert({ type: "success", message: `Deactivated ${target.templateName} v${target.version}.` });
            await loadAll();
        } catch (e: any) {
            setAlert({ type: "error", message: e?.message || "Deactivate failed." });
        } finally {
            setTemplatesInFlight(prev => {
                const next = new Set(prev);
                next.delete(target.templateGuid);
                return next;
            });
        }
    };

    const handleDownload = async (template: SurveyJSTemplateDto) => {
        setTemplatesInFlight(prev => new Set(prev).add(template.templateGuid));
        try {
            const { blob, filename } = await SurveyJSTemplateService.downloadTemplate(template.templateGuid);
            triggerBrowserDownload(blob, filename);
        } catch (e: any) {
            setAlert({ type: "error", message: e?.message || "Failed to download template." });
        } finally {
            setTemplatesInFlight(prev => {
                const next = new Set(prev);
                next.delete(template.templateGuid);
                return next;
            });
        }
    };

    const handleProvisionConfirmed = async () => {
        if (!confirmTarget) return;
        const flightKey = `${confirmTarget.corpSiteName}|${confirmTarget.clientGuidId}|${confirmTarget.templateGuid}`;
        setRowsInFlight(prev => new Set(prev).add(flightKey));

        const target = confirmTarget;
        const force = confirmForce;
        setConfirmTarget(undefined);
        setConfirmForce(false);

        try {
            const result: TemplateProvisioningResult = await SurveyJSTemplateService.provisionTenant(
                target.templateGuid, target.corpSiteName, target.clientGuidId, force
            );
            const alertType: any = result.outcome === "Success" ? "success"
                : result.outcome === "Skipped" ? "info"
                    : "error";
            setAlert({
                type: alertType,
                message: `${target.tenantDisplayName} (${target.corpSiteName}) · ${target.templateName} v${target.templateVersion}: ${result.message}`,
            });
            if (result.outcome === "Success") {
                await syncTenantAfterProvision(target.corpSiteName, target.clientGuidId);
            }
            await loadAll();
        } catch (e: any) {
            setAlert({ type: "error", message: `${target.tenantDisplayName}: ${e?.message || "Failed to provision tenant."}` });
        } finally {
            setRowsInFlight(prev => {
                const next = new Set(prev);
                next.delete(flightKey);
                return next;
            });
        }
    };

    const handleBulkProvision = async () => {
        if (activeTemplates.length === 0) return;
        setBulkLaunching(true);
        setBulkJobs([]);
        setAlert(undefined);
        try {
            const launches = await Promise.all(activeTemplates.map(t =>
                SurveyJSTemplateService.provisionAll(t.templateGuid, bulkForce)
                    .then(j => ({ template: t, jobGuid: j.jobGuid }))
            ));
            launches.forEach(({ template, jobGuid }) => pollJob(template, jobGuid));
        } catch (e: any) {
            setAlert({ type: "error", message: e?.message || "Failed to start bulk provisioning." });
            setBulkLaunching(false);
        }
    };

    const pollJob = async (template: SurveyJSTemplateDto, jobGuid: string) => {
        let stop = false;
        while (!stop) {
            try {
                const status = await SurveyJSTemplateService.getJobStatus(jobGuid);
                setBulkJobs(prev => {
                    const filtered = prev.filter(j => j.jobGuid !== jobGuid);
                    return [...filtered, status];
                });
                if (status.status === "Completed" || status.status === "Failed") stop = true;
            } catch {
                stop = true;
                setAlert({ type: "error", message: "Lost connection to a bulk job — refresh to see latest state." });
            }
            if (!stop) await new Promise(res => setTimeout(res, 2000));
        }
        const anyRunning = activeTemplates.length > 0 && bulkJobs.some(j => j.status !== "Completed" && j.status !== "Failed");
        if (!anyRunning) {
            setBulkLaunching(false);
            await syncTenantsAfterProvision();
            await loadAll();
        }
    };

    // ── Bottom: Tenants tree columns ───────────────────────────────────────

    const tenantColumns = useMemo(() => [
        {
            id: "name",
            accessorFn: (row: TreeRow) => row.nameCell,
            header: "Tenant / Template",
            cell: ({ row }: any) => {
                const r: TreeRow = row.original;
                if (r.kind === 'tenant') {
                    const canExpand = !!(r.subRows && r.subRows.length > 0);
                    const expanded = expandedTenants.has(r.rowKey);
                    return (
                        <span className={styles.TenantNameCell}>
                            <button
                                type="button"
                                className={styles.ChevronButton}
                                onClick={canExpand ? () => toggleTenant(r.rowKey) : undefined}
                                aria-label={expanded ? "Collapse" : "Expand"}
                                disabled={!canExpand}
                            >
                                {expanded ? "▼" : "▶"}
                            </button>
                            <span className={styles.TenantName}>{r.nameCell}</span>
                        </span>
                    );
                }
                return <span className={styles.ChildNameCell}>{r.nameCell}</span>;
            },
        },
        {
            id: "corpSite",
            header: "Corp site",
            cell: ({ row }: any) => row.original.kind === 'tenant' ? <>{row.original.corpSiteName}</> : <></>,
        },
        {
            id: "database",
            header: "Database",
            cell: ({ row }: any) => row.original.kind === 'tenant' ? <>{row.original.databaseName}</> : <></>,
        },
        {
            id: "status",
            header: "Status",
            cell: ({ row }: any) => {
                const r: TreeRow = row.original;
                if (r.kind === 'tenant') {
                    const cs = r.childStatuses ?? [];
                    const rollup = rollupStatusOf(cs);
                    return (
                        <span className={styles.StatusCell}>
                            <span className={dotClassForStatus(rollup)} />
                            {summarizeChildStatuses(cs)}
                        </span>
                    );
                }
                const ts = r.templateStatus ?? null;
                return (
                    <span className={styles.StatusCell}>
                        <span className={dotClassForStatus(ts)} />
                        {ts ? labelForStatus(ts) : "—"}
                    </span>
                );
            },
        },
        {
            id: "version",
            header: "Version",
            cell: ({ row }: any) => {
                const r: TreeRow = row.original;
                if (r.kind !== 'template') return <></>;
                const effective = r.syncedVersion ?? r.provisionedVersion ?? null;
                if (effective === null) return <>—</>;
                const differs = r.syncedVersion != null
                    && r.provisionedVersion != null
                    && r.syncedVersion !== r.provisionedVersion;
                const tooltip = differs
                    ? `On tenant: ${r.syncedVersion} · Last pushed: ${r.provisionedVersion}`
                    : undefined;
                return <span title={tooltip}>{effective}</span>;
            },
        },
        { id: "provisioned", header: "Provisioned", cell: ({ row }: any) => row.original.kind === 'template' ? <>{formatShortDate(row.original.provisionedDateUtc)}</> : <></> },
        { id: "by", header: "By", cell: ({ row }: any) => row.original.kind === 'template' ? <>{row.original.provisionedByName ?? "—"}</> : <></> },
        {
            id: "trigger",
            header: "Trigger",
            cell: ({ row }: any) => {
                const r: TreeRow = row.original;
                if (r.kind !== 'template') return <></>;
                if (r.sourceLabel === "External") {
                    return <span title="Version present on tenant but not from a tracked HSC push (e.g. pushed via the console app)">External</span>;
                }
                return <>{r.triggerKind ?? "—"}</>;
            },
        },
        {
            id: "error",
            header: "Last error",
            cell: ({ row }: any) => {
                const r: TreeRow = row.original;
                if (r.kind !== 'template') return <></>;
                return r.errorMessage
                    ? <span className={styles.ErrorCell} title={r.errorMessage}>{r.errorMessage}</span>
                    : <>—</>;
            },
        },
        {
            id: "actions",
            header: "Action",
            cell: ({ row }: any) => {
                const r: TreeRow = row.original;
                if (r.kind !== 'template') return <></>;
                const flightKey = `${r.tenantCorpSiteForAction}|${r.tenantClientGuidIdForAction}|${r.templateGuid}`;
                const inFlight = rowsInFlight.has(flightKey);
                const ts = r.templateStatus ?? null;
                const label = inFlight ? "Provisioning…"
                    : ts === "NotProvisioned" ? "Provision"
                        : ts === "Failed" ? "Retry"
                            : "Re-provision";

                const matchedTemplate = activeTemplates.find(t => t.templateGuid === r.templateGuid);
                const target: ProvisionTarget = {
                    templateGuid: r.templateGuid!,
                    moduleKey: r.moduleKey!,
                    templateName: matchedTemplate?.templateName ?? "",
                    templateVersion: matchedTemplate?.version ?? "",
                    corpSiteName: r.tenantCorpSiteForAction!,
                    clientGuidId: r.tenantClientGuidIdForAction!,
                    tenantDisplayName: r.tenantDisplayNameForAction!,
                    isOnCurrentVersion: !!r.isOnCurrentVersion,
                };

                return (
                    <div className={styles.ActionCell}>
                        <Button
                            text={label}
                            accent={ProjectAccents.CareCompliance}
                            disabled={inFlight}
                            onClick={() => {
                                setConfirmForce(ts === "OnCurrent");
                                setConfirmTarget(target);
                            }}
                        />
                        <Button
                            text="History"
                            accent={ProjectAccents.CareCompliance}
                            disabled={inFlight}
                            onClick={() => setHistoryTarget({
                                templateGuid: target.templateGuid,
                                templateName: target.templateName,
                                templateVersion: target.templateVersion,
                                corpSiteName: target.corpSiteName,
                                clientId: r.tenantClientIdForAction ?? 0,
                                tenantDisplayName: target.tenantDisplayName,
                            })}
                        />
                    </div>
                );
            },
        },
    ], [rowsInFlight, expandedTenants, toggleTenant, activeTemplates]);

    if (loading) {
        return (
            <div className={styles.Page}>
                <Spinner full />
            </div>
        );
    }

    return (
        <div className={styles.Page}>
            <div className={styles.HeaderRow}>
                <h1>SurveyJS Templates</h1>
                <h4>Manage SurveyJS templates and push them to Care Compliance tenants.</h4>
            </div>

            {alert ? (
                <Alert type={alert.type} closeable onClosed={() => setAlert(undefined)}>
                    {alert.message}
                </Alert>
            ) : null}

            <div className={styles.ActionsBar}>
                <Button
                    text="Upload new version"
                    accent={ProjectAccents.CareCompliance}
                    onClick={() => setUploadVisible(true)}
                />
                <Button
                    text={syncing ? "Syncing…" : "Sync from tenants"}
                    accent={ProjectAccents.CareCompliance}
                    disabled={syncing || bulkLaunching || activeTemplates.length === 0}
                    onClick={handleSync}
                />
                <span className={styles.LastSyncCaption}>
                    {lastSync
                        ? `Last synced: ${formatShortDate(lastSync.lastSyncedUtc)} by ${lastSync.lastSyncedByName}`
                        : "Last synced: Never"}
                </span>
                <div className={styles.ActionsSpacer} />
                {activeTemplates.length > 0 ? (
                    <>
                        <Button
                            text={bulkLaunching ? "Bulk provisioning…" : `Provision all (${activeTemplates.length} templates) to all tenants`}
                            accent={ProjectAccents.CareCompliance}
                            disabled={bulkLaunching || syncing}
                            onClick={handleBulkProvision}
                        />
                        <Checkbox
                            checked={bulkForce}
                            onChange={(value: any) => setBulkForce(!!value.checked)}
                            disabled={bulkLaunching}
                        />
                        <span>Force re-push</span>
                    </>
                ) : null}
            </div>

            {bulkJobs.length > 0 ? (
                <div className={styles.JobStats}>
                    {bulkJobs.map(j => (
                        <div key={j.jobGuid} className={styles.JobStat}>
                            <span className={styles.Label}>{j.templateName} v{j.version}</span>
                            <span className={styles.Value}>
                                {j.status} · {j.successCount}/{j.totalTenants} ok · {j.failureCount} fail · {j.skippedCount} skip
                            </span>
                        </div>
                    ))}
                </div>
            ) : null}

            <div className={styles.SectionHeading}>Active templates ({activeTemplates.length})</div>
            {activeTemplates.length === 0 ? (
                <div className={styles.ActiveTemplatesEmpty}>
                    No active templates yet. Use <b>Upload new version</b> above and activate it from the history dialog
                    to start provisioning.
                </div>
            ) : (
                <div className={styles.ActiveTemplatesPanel}>
                    {activeTemplates.map(t => {
                        const busy = templatesInFlight.has(t.templateGuid);
                        return (
                            <div key={t.templateGuid} className={styles.ActiveTemplateRow}>
                                <span className={styles.ActiveTemplateDot} />
                                <span className={styles.ActiveTemplateName}>
                                    <b>{t.templateName}</b> v{t.version}
                                </span>
                                <span className={styles.ActiveTemplateMeta}>
                                    {(t.moduleDisplayName || t.module)} / {(t.typeDisplayName || t.templateType)}
                                </span>
                                <span className={styles.ActiveTemplateMeta}>
                                    Provisioned to {t.provisionedCount} · Uploaded {formatShortDateOnly(t.uploadedDateUtc)} by {t.uploadedByName}
                                </span>
                                <span className={styles.ActiveTemplateActions}>
                                    <Button
                                        text={busy ? "…" : "Download"}
                                        accent={ProjectAccents.CareCompliance}
                                        disabled={busy}
                                        onClick={() => handleDownload(t)}
                                    />
                                    <Button
                                        text="View history"
                                        accent={ProjectAccents.CareCompliance}
                                        onClick={() => setHistoryType({ typeKey: t.templateType, typeDisplayName: t.typeDisplayName || t.templateType })}
                                    />
                                    <Button
                                        text="Deactivate"
                                        accent={ProjectAccents.CareCompliance}
                                        disabled={busy}
                                        onClick={() => setDeactivateTarget(t)}
                                    />
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className={styles.SectionHeading}>Tenants</div>
            <div className={styles.FilterRow}>
                <Searchbox
                    value={searchTerm}
                    onSearch={(value: any) => setSearchTerm(value)}
                    clearButton
                    placeholder="Search tenant / corp site / database"
                    style={{ width: 360 }}
                />
                <MultiSelect
                    data={DISPLAY_STATUS_OPTIONS}
                    textField="name"
                    valueField="value"
                    value={statusFilter}
                    onChange={(e: any) => setStatusFilter((e?.value ?? []) as DisplayStatus[])}
                    placeholder="Status"
                    selectionType="checkbox"
                    style={{ width: 220 }}
                />
                {distinctCorpSites.length > 1 ? (
                    <MultiSelect
                        data={distinctCorpSites.map(s => ({ name: s, value: s }))}
                        textField="name"
                        valueField="value"
                        value={corpSiteFilter}
                        onChange={(e: any) => setCorpSiteFilter((e?.value ?? []) as string[])}
                        placeholder="Corp site"
                        selectionType="checkbox"
                        style={{ width: 220 }}
                    />
                ) : null}
                <span className={styles.RowCount}>
                    {filteredTenantRows.length} of {treeRows.length} tenants
                </span>
            </div>

            <DataTable
                isLoading={false}
                columns={tenantColumns}
                data={visibleRows}
                accent={ProjectAccents.CareCompliance}
                height="60vh"
                noDataElement={
                    <div style={{ padding: 16, textAlign: "center", color: "#666" }}>
                        {activeTemplates.length === 0
                            ? "Upload and activate a template to populate this table."
                            : treeRows.length === 0
                                ? "No eligible tenants found."
                                : "No tenants match the current filters."}
                    </div>
                }
            />

            <UploadTemplateDialog
                visible={uploadVisible}
                types={types}
                onUploaded={handleUploaded}
                onCancel={() => setUploadVisible(false)}
            />

            <TemplateHistoryModal
                visible={!!historyType}
                typeKey={historyType?.typeKey ?? null}
                typeDisplayName={historyType?.typeDisplayName ?? ""}
                isBusyByGuid={templatesInFlight}
                onClose={() => setHistoryType(undefined)}
                onActivate={handleActivate}
                onDownload={handleDownload}
            />

            <Confirm
                visible={!!confirmTarget}
                type="question"
                accent={ProjectAccents.CareCompliance}
                heading="Provision tenant"
                onPrimaryButtonClick={handleProvisionConfirmed}
                onCancelButtonClick={() => { setConfirmTarget(undefined); setConfirmForce(false); }}
                onClose={() => { setConfirmTarget(undefined); setConfirmForce(false); }}
            >
                <div className={styles.ConfirmContent}>
                    {confirmTarget ? (
                        <div>
                            Provision <b>{confirmTarget.templateName} v{confirmTarget.templateVersion}</b> to{" "}
                            <b>{confirmTarget.tenantDisplayName}</b> ({confirmTarget.corpSiteName})?
                        </div>
                    ) : null}
                    {confirmTarget?.isOnCurrentVersion ? (
                        <Alert type="info">
                            This tenant is already on the current version. Provisioning will re-push the template.
                        </Alert>
                    ) : null}
                    <div className={styles.ConfirmCheckboxRow}>
                        <Checkbox
                            checked={confirmForce}
                            onChange={(value: any) => setConfirmForce(!!value.checked)}
                        />
                        <span>Force re-push (overrides the already-on-version skip)</span>
                    </div>
                </div>
            </Confirm>

            <Modal
                visible={rowsInFlight.size > 0}
                accent={ProjectAccents.CareCompliance}
                heading="Provisioning…"
                primaryButtonProps={{ buttonStyle: { display: "none" } }}
                cancelButtonProps={{ buttonStyle: { display: "none" } }}
            >
                <div className={styles.BlockingModalContent}>
                    <Spinner />
                    <div>Provisioning the tenant and refreshing its status — please wait.</div>
                </div>
            </Modal>

            <TenantProvisioningHistoryModal
                visible={!!historyTarget}
                target={historyTarget}
                onClose={() => setHistoryTarget(null)}
            />

            <Confirm
                visible={!!deactivateTarget}
                type="warning"
                accent={ProjectAccents.CareCompliance}
                heading="Deactivate template"
                onPrimaryButtonClick={handleDeactivateConfirmed}
                onCancelButtonClick={() => setDeactivateTarget(undefined)}
                onClose={() => setDeactivateTarget(undefined)}
            >
                <div className={styles.ConfirmContent}>
                    {deactivateTarget ? (
                        <div>
                            Deactivate <b>{deactivateTarget.templateName} v{deactivateTarget.version}</b> for{" "}
                            <b>{deactivateTarget.moduleDisplayName || deactivateTarget.module} / {deactivateTarget.typeDisplayName || deactivateTarget.templateType}</b>?
                        </div>
                    ) : null}
                    <Alert type="info">
                        Existing tenant deployments are not touched. New per-tenant provisioning will be rejected and the sync job will skip this template until another version is activated.
                    </Alert>
                </div>
            </Confirm>
        </div>
    );
}

export default SurveyJSTemplates;
